import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type { Patient, CreatePatientInput, UpdatePatientInput } from '@repo/shared'

interface PatientsFilters {
  search?: string
  status?: 'active' | 'inactive' | 'discharged'
  includeDeleted?: boolean
}

interface PatientWithFamily extends Patient {
  family_members?: Array<{
    id: string
    relationship: string
    whatsapp_number: string | null
    country_code: string
    is_primary_contact: boolean
    can_report: boolean
    profiles: {
      full_name: string
      phone: string | null
    }
  }>
}

async function fetchPatients(
  organizationId: string,
  filters?: PatientsFilters
): Promise<PatientWithFamily[]> {
  let query = supabase
    .from('patients')
    .select(`
      *,
      family_members (
        id,
        relationship,
        whatsapp_number,
        country_code,
        is_primary_contact,
        can_report,
        profiles (
          full_name,
          phone
        )
      )
    `)
    .eq('organization_id', organizationId)
    .neq('status', 'deleted')
    .order('full_name')

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  if (filters?.search) {
    query = query.ilike('full_name', `%${filters.search}%`)
  }

  const { data, error } = await query

  if (error) throw error
  return data as PatientWithFamily[]
}

export function usePatients(filters?: PatientsFilters) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['patients', organizationId, filters],
    queryFn: () => fetchPatients(organizationId!, filters),
    enabled: !!organizationId,
  })
}

export function useCreatePatient() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (input: Omit<CreatePatientInput, 'organization_id'>) => {
      const { data, error } = await supabase
        .from('patients')
        .insert({
          ...input,
          organization_id: currentOrganization!.id,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
  })
}

export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdatePatientInput & { id: string }) => {
      const { data, error } = await supabase
        .from('patients')
        .update(input)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patient', data.id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
  })
}

export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('patients')
        .update({
          status: 'deleted',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['admin-patients'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
  })
}
